import { useQueryClient } from "@tanstack/react-query";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";

import AddCardModal from "~/components/subscription/AddCardModal";
import { useAuth } from "~/contexts/AuthProvider";
import { useTeam } from "~/contexts/TeamProvider";
import { useStripeSession } from "~/hooks/useStripeSession";
import { useTeamPolling } from "~/hooks/useTeamPolling";
import { routes } from "~/router";

import { dueAutoptoOffer, isAutoptoCardRequired } from "../isAutoptoTrial";
import {
  clearCardSetupReturnPath,
  getCardSetupPaymentMethod,
  saveCardSetupPaymentMethod,
  saveCardSetupReturnPath,
} from "./cardSetupReturnPath";
import { notifyCardCheckLate } from "./notifyCardCheckLate";
import { paymentMethodSignature } from "./paymentMethodSignature";
import {
  PendingFirstOffer,
  readPendingFirstOffer,
  writePendingFirstOffer,
} from "./pendingFirstOffer";
import { CardDialogContext, CardDialogValue } from "./useCardDialog";

/**
 * The card dialog, one for the whole app: billing's "Add payment method"
 * and, during an Autopto trial, the countdown bar, the launch checklist and
 * the sections closed until a card all open it (one setup session at a
 * time: cancelling one expires them all). Stripe comes back with a full page load (the setup session's
 * return_url), to the page the dialog was opened on.
 *
 * The card lifts `autopto_card_required` through Stripe's webhook, a few
 * seconds later. SubscriptionSync hears it on the SubscriptionsChannel, but
 * the webhook may land while the page reloads, before the channel is
 * subscribed: the team is polled for a while too. The trial UI changes on
 * the refetched team, never optimistically. The billing page watches its
 * own payment method (useSavedCardRecheck).
 *
 * Until a watcher sees the card land (this one, billing's, the block
 * screens'), `isAwaitingCard` holds: the dialog stays shut and the card
 * buttons wait.
 *
 * The first card's webhook makes the one-time offer due too: once the card
 * is in, the offer page comes before the dashboard.
 */
export const CardDialogProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { user } = useAuth();
  const { teamId, team, refetchTeam } = useTeam();
  const queryClient = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();

  const {
    clientSecret,
    isModalOpen,
    isCreatingSession,
    // Stable, unlike openSession: the dialog stays the same across renders.
    createSession,
    closeModal,
  } = useStripeSession({ sessionType: "setup" });

  const isWaitingForCard = isAutoptoCardRequired(team);
  const blockedReason = team?.subscription?.blocked_reason ?? null;
  const offer = dueAutoptoOffer(team);
  // As billing shows it: the team carries the same subscription.
  const paymentMethod = paymentMethodSignature(
    team?.subscription?.payment_method,
  );

  // Back from Stripe (see CardSetupReturn): read below, after the team.
  const isAfterCardSetup = Boolean(location.state?.afterCardSetup);
  const loadedTeamId = team?.id;
  const [pendingFirstOffer, setPendingFirstOffer] =
    useState<PendingFirstOffer | null>(readPendingFirstOffer);

  useEffect(() => {
    writePendingFirstOffer(pendingFirstOffer);
  }, [pendingFirstOffer]);

  useEffect(() => {
    if (!pendingFirstOffer) return;
    if (
      teamId !== pendingFirstOffer.teamId ||
      location.pathname !== pendingFirstOffer.pathname
    ) {
      setPendingFirstOffer(null);

      return;
    }
    if (!team) return;
    if (offer) {
      setPendingFirstOffer(null);
      navigate(routes.autoptoOffer(), { replace: true });
    } else if (!team.autopto_card_required && !blockedReason) {
      setPendingFirstOffer(null);
    }
  }, [
    pendingFirstOffer,
    teamId,
    team,
    location.pathname,
    offer,
    blockedReason,
    navigate,
  ]);

  useEffect(() => {
    if (!pendingFirstOffer) return;
    const recheck = () => {
      if (document.visibilityState === "visible") void refetchTeam();
    };
    window.addEventListener("focus", recheck);
    document.addEventListener("visibilitychange", recheck);

    return () => {
      window.removeEventListener("focus", recheck);
      document.removeEventListener("visibilitychange", recheck);
    };
  }, [pendingFirstOffer, refetchTeam]);

  // The card Stripe came back with, until the team shows it. Above the
  // routes, so it outlives the redirects of a blocked team to billing (not
  // a reload).
  const [awaited, setAwaited] = useState<{
    teamId: number;
    // What the card should change: the trial's flag, a block, or the card
    // an offer's charge was declined on.
    kind: "trial" | "block" | "offer";
    // The block the card should change, null without one.
    blockedReason: string | null;
    // The payment method it replaces, undefined if unknown.
    paymentMethod: string | null | undefined;
    // Where Stripe brought the user back: the offer waits for a user who
    // has moved on meanwhile.
    pathname: string;
  } | null>(null);

  // The trial's flag lifts, or the block changes. A failed payment stays
  // (paying the invoice is the user's next step): there the card is in once
  // the subscription shows it. Two webhooks write the team's card (what
  // lifts a block) and the subscription's, in any order.
  const isStillAwaited =
    awaited !== null &&
    awaited.teamId === team?.id &&
    (awaited.kind === "trial"
      ? isWaitingForCard
      : awaited.kind === "offer"
        ? offer !== null && paymentMethod === awaited.paymentMethod
        : awaited.blockedReason === blockedReason &&
          (awaited.blockedReason !== "payment_failed" ||
            paymentMethod === awaited.paymentMethod));

  const {
    isPolling: isPollingTeam,
    startPolling,
    stopPolling,
  } = useTeamPolling(isStillAwaited);

  // A card saved for one team isn't awaited on another.
  useEffect(() => stopPolling, [teamId, stopPolling]);

  // Over once the card is in, on another team, or when the minute ran out
  // with it still awaited (told).
  useEffect(() => {
    if (!awaited || isPollingTeam) return;

    setAwaited(null);

    if (isStillAwaited) {
      if (
        awaited.kind === "trial" ||
        (awaited.kind === "block" &&
          awaited.blockedReason === "trial_ended_no_card")
      ) {
        setPendingFirstOffer({
          teamId: awaited.teamId,
          pathname: awaited.pathname,
        });
      }
      notifyCardCheckLate();
    } else if (
      awaited.kind === "trial" &&
      awaited.teamId === team?.id &&
      awaited.pathname === location.pathname &&
      offer
    ) {
      navigate(routes.autoptoOffer());
    }
  }, [
    awaited,
    isPollingTeam,
    isStillAwaited,
    team?.id,
    location.pathname,
    offer,
    navigate,
  ]);

  const [cardWaits, setCardWaits] = useState<string[]>([]);

  const reportCardWait = useCallback((source: string, isWaiting: boolean) => {
    setCardWaits((sources) => {
      if (isWaiting === sources.includes(source)) return sources;

      return isWaiting
        ? [...sources, source]
        : sources.filter((item) => item !== source);
    });
  }, []);

  // Also until the return is read: the buttons don't flash live.
  const isAwaitingCard =
    isAfterCardSetup || isPollingTeam || cardWaits.length > 0;

  const hasSubscription = Boolean(team?.subscription);

  // Read at the click: the dialog doesn't change with every navigation.
  const openCardDialog = useCallback(() => {
    if (isAwaitingCard) return;

    // A payment method kept by an earlier dialog mustn't outlive it.
    clearCardSetupReturnPath();
    saveCardSetupReturnPath(
      `${window.location.pathname}${window.location.search}`,
    );

    // The one the card replaces, for billing and a failed payment's block.
    if (hasSubscription) saveCardSetupPaymentMethod(paymentMethod);

    createSession();
  }, [createSession, isAwaitingCard, hasSubscription, paymentMethod]);

  const handleClose = useCallback(() => {
    clearCardSetupReturnPath();
    void closeModal();
  }, [closeModal]);

  // The billing page reads the flag and the replaced payment method as
  // well, in the same commit (its effects run first).
  useEffect(() => {
    if (!isAfterCardSetup || loadedTeamId === undefined) return;

    const replacedPaymentMethod = getCardSetupPaymentMethod();
    clearCardSetupReturnPath();

    if (isWaitingForCard || blockedReason) {
      setAwaited({
        teamId: loadedTeamId,
        kind: blockedReason ? "block" : "trial",
        blockedReason,
        paymentMethod: replacedPaymentMethod,
        pathname: location.pathname,
      });
      startPolling();
    } else if (offer && replacedPaymentMethod === null) {
      // The first card's webhook beat the page load.
      navigate(routes.autoptoOffer(), { replace: true });

      return;
    } else if (offer && replacedPaymentMethod !== undefined) {
      // Another card for the offer: its retry waits for it.
      setAwaited({
        teamId: loadedTeamId,
        kind: "offer",
        blockedReason: null,
        paymentMethod: replacedPaymentMethod,
        pathname: location.pathname,
      });
      startPolling();
    }

    // One-shot: history state survives reloads.
    navigate(`${location.pathname}${location.search}`, {
      replace: true,
      state: null,
    });
  }, [
    isAfterCardSetup,
    loadedTeamId,
    isWaitingForCard,
    blockedReason,
    offer,
    startPolling,
    navigate,
    location.pathname,
    location.search,
  ]);

  // A card raises the trial limits to the plan's and opens the domain:
  // what was read under the trial limits is stale. The sections probed by
  // useEndpointAccess recheck by themselves (the flag is in their key).
  const isCardRequired = Boolean(team?.autopto_card_required);
  const previous = useRef({ teamId: team?.id, isCardRequired });

  useEffect(() => {
    const { teamId: previousTeamId, isCardRequired: wasCardRequired } =
      previous.current;
    previous.current = { teamId: team?.id, isCardRequired };

    if (!team || team.id !== previousTeamId) return;
    if (!wasCardRequired || isCardRequired) return;

    for (const queryKey of [
      ["planUsage"],
      ["subscriptionPlans"],
      ["dashboard"],
      ["whiteLabel-hostnames"],
      ["autopto-domain-connected"],
    ]) {
      void queryClient.invalidateQueries({ queryKey });
    }
  }, [team, isCardRequired, queryClient]);

  const value = useMemo<CardDialogValue>(
    () => ({
      canAddCard: Boolean(user?.current_team_admin),
      openCardDialog,
      isOpeningCardDialog: isCreatingSession,
      isAwaitingCard,
      reportCardWait,
    }),
    [
      user?.current_team_admin,
      openCardDialog,
      isCreatingSession,
      isAwaitingCard,
      reportCardWait,
    ],
  );

  return (
    <CardDialogContext.Provider value={value}>
      {children}
      <AddCardModal
        isOpen={isModalOpen}
        clientSecret={clientSecret}
        isCreatingSession={isCreatingSession}
        onClose={handleClose}
      />
    </CardDialogContext.Provider>
  );
};
